import { type Draft, type PayloadAction } from "@reduxjs/toolkit"
import { type PanelRootState, type PanelStateBase } from "./create-panel-slice"

/**
 * Per-template chart view toggles, folded into the owning panel slice (asset-view / graph /
 * factors-view) so they persist per-panel and round-trip through a saved layout's viewConfig.
 *
 * Only ever `selectedCategory` and/or `selectedMetricDisplay`; both optional so each template stores
 * just the fields it owns. Defaults differ per template and live at the consumer's call site.
 */
export interface TemplateChartSettings {
  selectedCategory?: string | null
  selectedMetricDisplay?: string | null
}

/** Slice state carrying a per-template-key settings map. */
export interface WithTemplateCharts extends PanelStateBase {
  templateCharts: Partial<Record<string, TemplateChartSettings>>
}

export type SetTemplateChartPayload = PayloadAction<{
  dashboardId: string
  panelId: string
  templateKey: string
  settings: TemplateChartSettings
}>

/**
 * The two templateCharts reducers, shared by every panel slice that folds chart settings. `ensure`
 * comes from the slice factory; the draft is the plain object at runtime (the factory's documented
 * `as unknown as` shim), so we operate on `PanelRootState<S>` to sidestep the generic-`Draft<S>`
 * non-simplification (see create-panel-slice / project memory).
 */
export function makeTemplateChartReducers<S extends WithTemplateCharts>(
  ensure: (draft: Draft<PanelRootState<S>>, dashboardId: string, panelId: string) => S,
) {
  return {
    // Merge a partial settings change for one template key (header/chart toggles).
    setTemplateChartSetting(state: Draft<PanelRootState<S>>, action: SetTemplateChartPayload) {
      const { dashboardId, panelId, templateKey, settings } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      instance.templateCharts = {
        ...instance.templateCharts,
        [templateKey]: { ...instance.templateCharts[templateKey], ...settings },
      }
    },

    // Seed a template key's initial settings (e.g. from a drilldown). No-op once the key exists, so
    // a saved/loaded preference or a prior seed is never overwritten (mirrors the rule-33 guard).
    seedTemplateChart(state: Draft<PanelRootState<S>>, action: SetTemplateChartPayload) {
      const { dashboardId, panelId, templateKey, settings } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      if (instance.templateCharts[templateKey] != null) return
      instance.templateCharts = { ...instance.templateCharts, [templateKey]: settings }
    },
  }
}
